import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Clock, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { timeEntriesApi, laborApi, employeesApi, projectsApi } from '../api';
import { fmtDate } from '../constants';
import { TIME_STATUS_OPTIONS, TIME_STATUS_STYLES, timeStatusLabel, fmtMinutes, payDisplay, costDisplay } from '../laborConstants';
import { TimeEntryFormDialog } from './TimeEntryFormDialog';

const selectCls = 'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';

const SummaryPill = ({ label, value, testId }) => (
  <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg px-4 py-3" data-testid={testId}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-lg font-semibold text-white mt-0.5">{value}</div>
  </div>
);

export const TimeEntriesView = ({ lockedProjectId, lockedEmployeeId, showLaborCost = true, showPay = true, disableAdd = false, refreshKey = 0, onChanged }) => {
  const lockedP = lockedProjectId !== undefined && lockedProjectId !== null;
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ status: '', employee_id: '', project_scope: 'all', start_date: '', end_date: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeEntry, setActiveEntry] = useState(null);

  useEffect(() => {
    if (!lockedEmployeeId) employeesApi.list({}).then((r) => setEmployees(r.data)).catch(() => {});
    if (!lockedP) projectsApi.list({ sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
  }, [lockedEmployeeId, lockedP]);

  const buildParams = useCallback(() => {
    const p = {};
    if (lockedP) p.project_id = lockedProjectId;
    else if (filters.project_scope === 'general') p.general_time_only = true;
    else if (filters.project_scope !== 'all') p.project_id = filters.project_scope;
    if (lockedEmployeeId) p.employee_id = lockedEmployeeId;
    else if (filters.employee_id) p.employee_id = filters.employee_id;
    if (filters.start_date) p.start_date = filters.start_date;
    if (filters.end_date) p.end_date = filters.end_date;
    return p;
  }, [lockedP, lockedProjectId, lockedEmployeeId, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = buildParams();
      const [listRes, sumRes] = await Promise.all([
        timeEntriesApi.list(filters.status ? { ...p, status: filters.status } : p),
        laborApi.summary(p),
      ]);
      setEntries(listRes.data);
      setSummary(sumRes.data);
    } catch {
      toast.error('Could not load time entries');
    } finally { setLoading(false); }
  }, [buildParams, filters.status]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setF = (k, v) => setFilters((x) => ({ ...x, [k]: v }));

  const quickApprove = async (e, entry) => {
    e.stopPropagation();
    try {
      await timeEntriesApi.approve(entry.id);
      toast.success('Time entry approved');
      load();
      onChanged?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not approve entry');
    }
  };

  return (
    <div data-testid="time-entries-view">
      {/* Approved summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryPill label="Approved Time" value={fmtMinutes(summary.approved_minutes)} testId="te-sum-minutes" />
          <SummaryPill label="Approved Entries" value={String(summary.approved_entry_count)} testId="te-sum-count" />
          {showLaborCost && <SummaryPill label="Recorded Labor Cost" value={costDisplay(summary.recorded_labor_cost_cents)} testId="te-sum-cost" />}
          {showPay && <SummaryPill label="Calculable Pay Earned" value={payDisplay(summary.calculable_pay_earned_cents)} testId="te-sum-pay" />}
        </div>
      )}
      {summary && (summary.missing_labor_cost_entry_count > 0 || summary.uncalculated_pay_entry_count > 0) && (
        <p className="text-xs text-amber-400/90 mb-4" data-testid="te-sum-warning">
          {summary.missing_labor_cost_entry_count > 0 && `${summary.missing_labor_cost_entry_count} approved ${summary.missing_labor_cost_entry_count === 1 ? 'entry has' : 'entries have'} no internal cost rate. `}
          {summary.uncalculated_pay_entry_count > 0 && `${summary.uncalculated_pay_entry_count} approved ${summary.uncalculated_pay_entry_count === 1 ? 'entry has' : 'entries have'} no calculable pay.`}
          {' '}Totals only include known amounts — missing rates are never treated as $0.
        </p>
      )}

      {/* Filters + add */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <select value={filters.status} onChange={(e) => setF('status', e.target.value)} className={selectCls} data-testid="te-filter-status">
          <option value="">All statuses</option>
          {TIME_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {!lockedEmployeeId && (
          <select value={filters.employee_id} onChange={(e) => setF('employee_id', e.target.value)} className={selectCls} data-testid="te-filter-employee">
            <option value="">All employees</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.display_name}</option>)}
          </select>
        )}
        {!lockedP && (
          <select value={filters.project_scope} onChange={(e) => setF('project_scope', e.target.value)} className={selectCls} data-testid="te-filter-project">
            <option value="all">All time</option>
            <option value="general">General BuildBroz only</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <Input type="date" value={filters.start_date} onChange={(e) => setF('start_date', e.target.value)} className={`${inputCls} w-auto`} data-testid="te-filter-start" />
        <Input type="date" value={filters.end_date} onChange={(e) => setF('end_date', e.target.value)} className={`${inputCls} w-auto`} data-testid="te-filter-end" />
        {!disableAdd && (
          <Button onClick={() => { setActiveEntry(null); setDialogOpen(true); }} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold ml-auto" data-testid="te-add-btn">
            <Plus size={16} /> Add Time Entry
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">
          <Loader2 size={26} className="mx-auto mb-3 animate-spin text-[#D4AF37]" /> Loading time entries…
        </div>
      ) : entries.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="te-empty">
          <Clock size={32} className="mx-auto mb-3 text-gray-700" />
          <p className="text-gray-500">No time entries yet. Draft entries do not affect totals until approved.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="te-list">
          {entries.map((en) => (
            <button
              key={en.id}
              onClick={() => { setActiveEntry(en); setDialogOpen(true); }}
              className={`w-full flex flex-wrap items-center gap-x-4 gap-y-1 bg-[#111111] hover:bg-[#1A1A1A] border border-gray-800 rounded-lg px-4 py-3 text-left transition-colors ${en.status === 'voided' ? 'opacity-60' : ''}`}
              data-testid={`te-row-${en.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium">{en.employee?.display_name}</span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${TIME_STATUS_STYLES[en.status]}`}>{timeStatusLabel(en.status)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">
                  {fmtDate(en.work_date)} · {en.project ? en.project.name : 'General BuildBroz'}
                  {en.work_category ? ` · ${en.work_category}` : ''}
                </div>
              </div>
              <div className="text-sm text-gray-200 font-medium shrink-0">{fmtMinutes(en.duration_minutes)}</div>
              {showPay && (
                <div className="text-xs text-gray-400 shrink-0 w-28 text-right">
                  Pay: <span className="text-gray-200">{payDisplay(en.pay_amount_cents)}</span>
                </div>
              )}
              {showLaborCost && (
                <div className="text-xs text-gray-400 shrink-0 w-28 text-right">
                  Cost: <span className="text-gray-200">{costDisplay(en.labor_cost_amount_cents)}</span>
                </div>
              )}
              {en.status === 'draft' && (
                <span
                  role="button"
                  onClick={(e) => quickApprove(e, en)}
                  className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 border border-green-900 rounded-md px-2 py-1 shrink-0"
                  data-testid={`te-approve-${en.id}`}
                >
                  <CheckCircle2 size={13} /> Approve
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <TimeEntryFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entry={activeEntry}
        lockedProjectId={lockedP ? lockedProjectId : undefined}
        lockedEmployeeId={lockedEmployeeId}
        onSaved={() => { load(); onChanged?.(); }}
      />
    </div>
  );
};
